import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { PanelMenu } from 'primereact/panelmenu';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import superAdminBorrowerReportService from '../../services/admin/superAdminBorrowerReportService';
import { getReportComponent, determineReportType } from '../../config/reportTypeMapping';
import InputDataCard from '../../components/reports/input/InputDataCard';

// ─── report type → display name map ──────────────────────────────────────────
const REPORT_NAMES = {
    'PAN v1':               ['PAN Details', 'PAN_DETAILS', 'PAN v1'],
    'PAN v2':               ['PAN Details V2', 'PAN_V2', 'PAN v2'],
    'Voter ID v1':          ['Voter ID', 'VOTER_ID', 'Voter ID v1'],
    'Voter ID v2':          ['Voter ID V2', 'VOTER_ID_V2', 'Voter ID v2'],
    'Driving Licence v1':   ['Driving Licence', 'DL_DETAILED', 'Driving Licence v1'],
    'Driving Licence v2':   ['Driving Licence V2', 'DL_V2', 'Driving Licence v2'],
    'RC Details':           ['RC Details', 'RC_DETAILS'],
    'CA Membership':        ['CA Membership', 'CA_MEMBERSHIP'],
    'Equifax':              ['Equifax', 'BUREAU_REPORT', 'Equifax Report'],
    'Experian':             ['Experian', 'EXPERIAN_REPORT'],
    'Bank Statement':       ['Bank Statement Analysis', 'BANK_STATEMENT'],
    'Mobile Bureau':        ['Mobile Bureau Look up', 'MOBILE_BUREAU'],
    'Employment by Mobile': ['Employment History by Mobile', 'EMPLOYMENT_HISTORY_MOBILE'],
    'Employment by UAN':    ['Employment History by UAN', 'EMPLOYMENT_HISTORY_UAN'],
    'Account Aggregator':   ['Account Aggregator', 'ACCOUNT_AGGREGATOR'],
};

const STATUS_OPTIONS = [
    { label: 'All Statuses', value: null },
    { label: 'Fetched',      value: 'fetched' },
    { label: 'Failed',       value: 'failed' },
    { label: 'Pending',      value: 'pending' },
];

const STATUS_SEVERITY = { fetched: 'success', failed: 'danger', pending: 'warning' };

// ─── left sidebar menu ────────────────────────────────────────────────────────
const buildMenuModel = (onSelect, activeKey) => {
    const item = (label, icon, key) => ({
        label,
        icon,
        className: activeKey === key ? 'active-menu-item' : '',
        command: () => onSelect(key),
    });

    return [
        {
            label: 'Identity Verification',
            icon: 'pi pi-id-card',
            expanded: true,
            items: [
                item('PAN v1',             'pi pi-credit-card',  'PAN v1'),
                item('PAN v2',             'pi pi-credit-card',  'PAN v2'),
                item('Voter ID v1',        'pi pi-user',         'Voter ID v1'),
                item('Voter ID v2',        'pi pi-user',         'Voter ID v2'),
                item('Driving Licence v1', 'pi pi-car',          'Driving Licence v1'),
                item('Driving Licence v2', 'pi pi-car',          'Driving Licence v2'),
                item('RC Details',         'pi pi-tag',          'RC Details'),
                item('CA Membership',      'pi pi-briefcase',    'CA Membership'),
            ],
        },
        {
            label: 'Financial Reports',
            icon: 'pi pi-chart-line',
            expanded: true,
            items: [
                item('Equifax',              'pi pi-chart-line',  'Equifax'),
                item('Experian',             'pi pi-chart-bar',   'Experian'),
                item('Bank Statement',       'pi pi-building',    'Bank Statement'),
                item('Mobile Bureau',        'pi pi-mobile',      'Mobile Bureau'),
                item('Employment by Mobile', 'pi pi-briefcase',   'Employment by Mobile'),
                item('Employment by UAN',    'pi pi-database',    'Employment by UAN'),
                item('Account Aggregator',   'pi pi-wallet',      'Account Aggregator'),
            ],
        },
        {
            label: 'Report History',
            icon: 'pi pi-history',
            className: activeKey === '__history__' ? 'active-menu-item' : '',
            command: () => onSelect('__history__'),
        },
    ];
};

// ─── component ────────────────────────────────────────────────────────────────
const SuperAdminBorrowerReports = () => {
    const toast = useRef(null);

    // ── navigation state ─────────────────────────────────────────────────────
    const [activeKey, setActiveKey] = useState('__history__');

    // ── list state ───────────────────────────────────────────────────────────
    const [reports, setReports]           = useState([]);
    const [loading, setLoading]           = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst]               = useState(0);
    const [rows, setRows]                 = useState(15);

    // ── detail state ─────────────────────────────────────────────────────────
    const [selectedReport, setSelectedReport]   = useState(null);
    const [reportDetails, setReportDetails]     = useState(null);
    const [detailsLoading, setDetailsLoading]   = useState(false);
    const reportViewRef = useRef(null);

    // ── filters ──────────────────────────────────────────────────────────────
    const [searchInput, setSearchInput]           = useState('');
    const [search, setSearch]                     = useState('');
    const [selectedTenant, setSelectedTenant]     = useState(null);
    const [selectedStatus, setSelectedStatus]     = useState(null);
    const [fromDate, setFromDate]                 = useState(null);
    const [toDate, setToDate]                     = useState(null);

    // ── tenant dropdown ───────────────────────────────────────────────────────
    const [tenantOptions, setTenantOptions]       = useState([{ label: 'All Tenants', value: null }]);

    // ── derived: which report_names match the active sidebar key ─────────────
    const activeReportNames = activeKey === '__history__' ? null : (REPORT_NAMES[activeKey] || null);

    // ─────────────────────────────────────────────────────────────────────────
    // Fetch tenants for dropdown
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        superAdminBorrowerReportService.getTenants().then(res => {
            if (res?.success && Array.isArray(res.data)) {
                setTenantOptions([
                    { label: 'All Tenants', value: null },
                    ...res.data.map(t => ({ label: t.name || t.id, value: t.id })),
                ]);
            }
        });
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // Fetch reports
    // ─────────────────────────────────────────────────────────────────────────
    const fetchReports = useCallback(async (pageFirst = first, pageRows = rows) => {
        setLoading(true);
        setSelectedReport(null);
        setReportDetails(null);

        const params = {
            page:     Math.floor(pageFirst / pageRows) + 1,
            per_page: pageRows,
        };
        if (search)                       params.search      = search;
        if (selectedTenant)               params.tenant_id   = selectedTenant;
        if (selectedStatus)               params.status      = selectedStatus;
        if (fromDate)                     params.from_date   = fromDate.toISOString().split('T')[0];
        if (toDate)                       params.to_date     = toDate.toISOString().split('T')[0];
        if (activeReportNames)            params.report_names = activeReportNames.join(',');

        const res = await superAdminBorrowerReportService.getReports(params);
        if (res?.success) {
            setReports(res.data || []);
            setTotalRecords(res.meta?.total || 0);
        } else {
            setReports([]);
            setTotalRecords(0);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: res?.message || 'Failed to load reports', life: 4000 });
        }
        setLoading(false);
    }, [first, rows, search, selectedTenant, selectedStatus, fromDate, toDate, activeReportNames]);

    useEffect(() => {
        setFirst(0);
        setSelectedReport(null);
        setReportDetails(null);
    }, [activeKey, search, selectedTenant, selectedStatus, fromDate, toDate]);

    useEffect(() => {
        fetchReports(first, rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [first, rows, search, selectedTenant, selectedStatus, fromDate, toDate, activeKey]);

    // ─────────────────────────────────────────────────────────────────────────
    // Load detail when a row is selected
    // ─────────────────────────────────────────────────────────────────────────
    const handleRowSelect = async (report) => {
        if (selectedReport?.id === report.id) {
            setSelectedReport(null);
            setReportDetails(null);
            return;
        }
        setSelectedReport(report);
        setReportDetails(null);

        // Scroll to detail view
        setTimeout(() => reportViewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);

        if (!report.report_json) {
            setDetailsLoading(true);
            const res = await superAdminBorrowerReportService.getReport(report.id);
            setDetailsLoading(false);
            if (res?.success) {
                setReportDetails(res.data);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load report details', life: 3000 });
            }
        } else {
            setReportDetails(report);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render report output
    // ─────────────────────────────────────────────────────────────────────────
    const renderReportOutput = (detail) => {
        if (!detail?.report_json) return <Message severity="info" text="No report data available" />;

        const reportType = determineReportType(detail);
        const config     = getReportComponent(reportType);

        if (!config) return <Message severity="warn" text={`No viewer for report type: ${reportType}`} />;

        const { component: ReportComponent, props: getProps } = config;
        const props = getProps ? getProps(detail.report_json, detail) : { data: detail.report_json };

        return <ReportComponent {...props} hideCredits={true} />;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Page / lazy handler
    // ─────────────────────────────────────────────────────────────────────────
    const onPage = (e) => {
        setFirst(e.first);
        setRows(e.rows);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Table templates
    // ─────────────────────────────────────────────────────────────────────────
    const statusTemplate = (row) => (
        <Tag value={row.status} severity={STATUS_SEVERITY[row.status] || 'info'} />
    );

    const dateTemplate = (row) => row.fetched_date
        ? new Date(row.fetched_date).toLocaleDateString('en-IN')
        : '—';

    const actionsTemplate = (row) => (
        <Button
            icon={selectedReport?.id === row.id ? 'pi pi-times' : 'pi pi-eye'}
            className="p-button-text p-button-sm p-button-rounded"
            aria-label="View report"
            tooltip={selectedReport?.id === row.id ? 'Close' : 'View report'}
            tooltipOptions={{ position: 'top' }}
            onClick={() => handleRowSelect(row)}
        />
    );

    const rowClassName = (row) =>
        selectedReport?.id === row.id ? 'highlighted-row' : '';

    // ─────────────────────────────────────────────────────────────────────────
    // Header for report table
    // ─────────────────────────────────────────────────────────────────────────
    const tableHeader = (
        <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <span className="text-900 font-semibold text-lg">
                {activeKey === '__history__' ? 'All Reports' : activeKey}
            </span>
            <div className="flex flex-wrap gap-2 align-items-center">
                {/* Search */}
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') setSearch(searchInput); }}
                        placeholder="Search name / PAN..."
                        style={{ width: '200px' }}
                    />
                </span>

                {/* Tenant filter */}
                <Dropdown
                    value={selectedTenant}
                    options={tenantOptions}
                    onChange={e => setSelectedTenant(e.value)}
                    placeholder="All Tenants"
                    style={{ width: '180px' }}
                />

                {/* Status filter */}
                <Dropdown
                    value={selectedStatus}
                    options={STATUS_OPTIONS}
                    onChange={e => setSelectedStatus(e.value)}
                    placeholder="All Statuses"
                    style={{ width: '140px' }}
                />

                {/* Date range */}
                <Calendar
                    value={fromDate}
                    onChange={e => setFromDate(e.value)}
                    placeholder="From Date"
                    showIcon
                    style={{ width: '150px' }}
                    dateFormat="dd/mm/yy"
                />
                <Calendar
                    value={toDate}
                    onChange={e => setToDate(e.value)}
                    placeholder="To Date"
                    showIcon
                    style={{ width: '150px' }}
                    dateFormat="dd/mm/yy"
                />

                {/* Search button */}
                <Button
                    icon="pi pi-search"
                    label="Search"
                    className="p-button-sm"
                    onClick={() => setSearch(searchInput)}
                />

                {/* Clear */}
                <Button
                    icon="pi pi-filter-slash"
                    label="Clear"
                    className="p-button-sm p-button-secondary p-button-outlined"
                    onClick={() => {
                        setSearchInput('');
                        setSearch('');
                        setSelectedTenant(null);
                        setSelectedStatus(null);
                        setFromDate(null);
                        setToDate(null);
                    }}
                />
            </div>
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Build sidebar menu model (re-build on activeKey change)
    // ─────────────────────────────────────────────────────────────────────────
    const menuModel = buildMenuModel(setActiveKey, activeKey);

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="flex" style={{ minHeight: '80vh' }}>
            <Toast ref={toast} />

            {/* ── Left Sidebar ── */}
            <div
                className="surface-section border-right-1 surface-border"
                style={{ width: '260px', minWidth: '260px', padding: '1rem 0' }}
            >
                <div className="px-3 mb-3">
                    <span className="text-900 font-bold text-lg">Borrower Reports</span>
                </div>

                <style>{`
                    .sa-br-menu .p-panelmenu-header-action,
                    .sa-br-menu .p-menuitem-link {
                        padding: 0.6rem 1rem !important;
                        border-radius: 0 !important;
                    }
                    .sa-br-menu .p-menuitem-link:hover,
                    .sa-br-menu .p-panelmenu-header-action:hover {
                        background: var(--surface-100) !important;
                    }
                    .sa-br-menu .active-menu-item .p-menuitem-link,
                    .sa-br-menu .active-menu-item > .p-panelmenu-header-action {
                        background: var(--primary-100) !important;
                        color: var(--primary-color) !important;
                        font-weight: 600;
                    }
                    .highlighted-row {
                        background: var(--primary-50) !important;
                    }
                `}</style>

                <PanelMenu
                    model={menuModel}
                    className="sa-br-menu"
                    style={{ border: 'none', boxShadow: 'none' }}
                />
            </div>

            {/* ── Right Content ── */}
            <div className="flex-1 p-3" style={{ minWidth: 0 }}>

                {/* Report list table */}
                <div className="card">
                    <DataTable
                        value={reports}
                        lazy
                        paginator
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        rowsPerPageOptions={[10, 15, 25, 50]}
                        loading={loading}
                        header={tableHeader}
                        emptyMessage="No reports found."
                        dataKey="id"
                        rowClassName={rowClassName}
                        className="p-datatable-sm"
                        responsiveLayout="scroll"
                    >
                        <Column field="id"          header="ID"          sortable style={{ width: '60px' }} />
                        <Column field="tenant_id"   header="Tenant"      sortable style={{ width: '120px' }} />
                        <Column field="lead_name"   header="Lead Name"   sortable />
                        <Column field="pan"         header="PAN"         sortable style={{ width: '110px' }} />
                        <Column field="report_name" header="Report Type" sortable style={{ width: '160px' }} />
                        <Column field="status"      header="Status"      body={statusTemplate} style={{ width: '100px' }} />
                        <Column field="fetched_date" header="Date"       body={dateTemplate}   style={{ width: '100px' }} />
                        <Column body={actionsTemplate} style={{ width: '60px', textAlign: 'center' }} />
                    </DataTable>
                </div>

                {/* ── Inline report detail ── */}
                {selectedReport && (
                    <div ref={reportViewRef} className="card mt-3">
                        <div className="flex justify-content-between align-items-center mb-3">
                            <div>
                                <span className="text-900 font-bold text-xl">
                                    {selectedReport.report_name}
                                </span>
                                <span className="text-500 ml-3">
                                    {selectedReport.lead_name} · {selectedReport.pan} · Tenant: {selectedReport.tenant_id}
                                </span>
                            </div>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                aria-label="Close"
                                onClick={() => { setSelectedReport(null); setReportDetails(null); }}
                            />
                        </div>

                        <Divider />

                        {detailsLoading ? (
                            <div className="flex justify-content-center py-5">
                                <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                            </div>
                        ) : reportDetails ? (
                            <>
                                {/* Input data (params used to generate the report) */}
                                {reportDetails.report_json && (
                                    <div className="mb-4">
                                        <InputDataCard reportData={reportDetails.report_json} reportName={reportDetails.report_name} />
                                    </div>
                                )}

                                <Divider />

                                {/* Output / rendered report */}
                                <div className="mt-3">
                                    {renderReportOutput(reportDetails)}
                                </div>
                            </>
                        ) : (
                            <Message severity="info" text="Loading report details..." />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuperAdminBorrowerReports;
